import React, { useEffect, useMemo, useState } from 'react';
import DocumentOverlay from './DocumentOverlay.js';
import { downloadBlob, normalizePhoneLocal } from '../services/outboundShare.js';

export const selectedShareFiles = (share) => {
    const selected = share.template?.default_attachments ?? ['pdf', 'image'];
    const name = share.fileBaseName || 'document';
    return [
        selected.includes('pdf') && share.pdfBlob && new File([share.pdfBlob], name + '.pdf', { type: 'application/pdf' }),
        selected.includes('image') && share.imageBlob && new File([share.imageBlob], name + '.jpg', { type: 'image/jpeg' }),
    ].filter(Boolean);
};
export default function WhatsAppSharePreview({ share, onClose, logEvent, onPrepareAttachments, onOpenPrint }) {
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [attachment, setAttachment] = useState('');
    useEffect(() => {
        if (share.stage === 'ready') {
            setPhone(share.initialPhone || share.phone?.raw || '');
            setMessage(share.message || '');
            setNotice('');
        }
    }, [share.stage, share.initialPhone, share.message, share.phone]);
    const norm = useMemo(() => normalizePhoneLocal(phone), [phone]);
    const availableFiles = useMemo(() => selectedShareFiles(share), [share]);
    const requestedAttachments = share.template?.default_attachments ?? ['pdf', 'image'];
    const selectedFile = availableFiles.find(file => file.type === attachment) || availableFiles[0];
    const files = useMemo(() => selectedFile ? [selectedFile] : [], [selectedFile]);
    let canShare = false;
    try { canShare = Boolean(files.length && navigator.share && navigator.canShare?.({ files })); } catch (_) { /* download fallback */ }
    const record = (event, meta = {}) => {
        Promise.resolve(logEvent(event, meta, { message_text: message, phone: norm.e164 || phone }))
            .then((result) => { if (!result) setNotice('تعذر حفظ سجل المحاولة. فتح واتساب لا يؤكد الإرسال أو التسليم.'); })
            .catch(() => setNotice('تعذر حفظ سجل محاولة المشاركة.'));
    };
    const openWhatsApp = () => {
        if (!norm.valid || !message.trim()) return;
        // The anchor follows the link directly from this user gesture (no async popup).
        record('whatsapp_opened', { method: 'wa-me', attachments: 'manual' });
        setNotice('رابط واتساب ينقل النص فقط؛ أرفق الملفات المحددة يدويًا. لا يوجد تأكيد إرسال أو تسليم.');
    };
    const shareFiles = async () => {
        if (!canShare || busy) return;
        setBusy(true);
        try {
            const action = navigator.share({ files, text: message });
            record('share_sheet_opened', { files: files.map((f) => f.type).join(',') });
            await action;
            setNotice('انتهت نافذة المشاركة. هذا ليس تأكيد تسليم من واتساب.');
        } catch (error) {
            if (error?.name === 'AbortError') {
                record('user_cancelled', { at: 'share_sheet' });
                setNotice('أُلغيت المشاركة. يمكنك المحاولة مجددًا.');
            }
            else setNotice('تعذرت المشاركة؛ نزّل الملفات وأرفقها يدويًا.');
        } finally { setBusy(false); }
    };
    const download = () => {
        files.forEach((file) => downloadBlob(file, file.name));
        record('files_downloaded', { files: files.map((f) => f.type).join(',') });
        setNotice('تم طلب تنزيل المرفقات المحددة في القالب.');
    };
    const copy = async () => {
        try { await navigator.clipboard.writeText(message); record('text_copied'); setNotice('تم نسخ النص.'); }
        catch (_) { setNotice('تعذر النسخ؛ انسخ النص يدويًا.'); }
    };
    const openPrint = () => {
        try { onOpenPrint?.(); }
        catch (error) { setNotice(error?.message || 'تعذر فتح نسخة الطباعة.'); }
    };
    if (!share.stage || share.stage === 'idle') return null;
    const whatsappUrl = norm.valid && message.trim() ? 'https://wa.me/' + norm.wa + '?text=' + encodeURIComponent(message) : undefined;
    return <DocumentOverlay onClose={onClose} labelledBy="whatsapp-preview-title" testId="whatsapp-share-preview" compact>
        <header className="document-heading">
            <h3 id="whatsapp-preview-title">معاينة رسالة واتساب</h3>
            <button type="button" onClick={onClose}>رجوع</button>
        </header>
        <div className="document-scroll document-form-body">
            {share.stage === 'preparing' && <p role="status">جارٍ التحقق من بيانات المستند وتجهيز النص… يمكنك الرجوع وإعادة المحاولة.</p>}
            {share.stage === 'error' && <div role="alert" className="document-error">{share.message}
                <button type="button" onClick={onClose}>رجوع للمستند وإعادة المحاولة</button>
            </div>}
            {share.stage === 'ready' && <>
                <p>النص جاهز. يمكنك فتح واتساب الآن، أو تجهيز المرفق ثم مشاركته. لا يتم الإرسال تلقائيًا.</p>
                <p>{share.documentNumber} · {share.template?.name || share.template?.action_key}</p>
                {share.assetStage === 'preparing' && <p role="status" data-testid="whatsapp-attachment-progress">جارٍ تجهيز المرفق… فتح النص متاح أسفل النافذة.</p>}
                {share.assetStage === 'error' && <div role="alert" className="document-error" data-testid="whatsapp-attachment-error">
                    {share.assetError} <p>نص الرسالة ما زال متاحًا. لم يتم إرفاق أي ملف.</p>
                </div>}
                <div className="document-share-actions">
                    {onPrepareAttachments && requestedAttachments.length > 0 && ['idle','error','preparing'].includes(share.assetStage) &&
                        <button type="button" data-testid="whatsapp-prepare-attachments" disabled={share.assetStage === 'preparing'} onClick={onPrepareAttachments}>
                            {share.assetStage === 'error' ? 'إعادة تجهيز المرفق' : 'تجهيز PDF / الصورة'}
                        </button>}
                    {onOpenPrint && requestedAttachments.length > 0 && <button type="button" onClick={openPrint} data-testid="whatsapp-print-fallback">نسخة الطباعة / حفظ PDF</button>}
                    {availableFiles.length > 1 && <label>المرفق
                        <select aria-label="المرفق للمشاركة" value={selectedFile?.type || ''} onChange={e => setAttachment(e.target.value)}>
                            {availableFiles.map(file => <option key={file.type} value={file.type}>{file.type === 'application/pdf' ? 'PDF' : 'صورة'}</option>)}
                        </select>
                    </label>}
                    {canShare && <button type="button" className="document-primary" data-testid="whatsapp-share-file-button" disabled={busy || !message.trim()} onClick={shareFiles}>مشاركة {selectedFile?.type === 'application/pdf' ? 'PDF' : 'الصورة'}</button>}
                    {files.length > 0 && <button type="button" onClick={download}>تنزيل المرفق المحدد</button>}
                </div>
                {!canShare && files.length > 0 && <p>مشاركة الملف مباشرة غير متاحة في هذا المتصفح. نزّله ثم أرفقه من واتساب.</p>}
                <p data-testid="whatsapp-share-attachments">مرفقات القالب: {requestedAttachments.length ? requestedAttachments.map(type => type === 'pdf' ? 'PDF' : 'صورة').join(' + ') : 'نص فقط'}.
                    {requestedAttachments.length > 0 && (availableFiles.length ? ' الملفات جاهزة. المشاركة بمرفق واحد في كل مرة.' : ' لم تُجهّز الملفات بعد؛ رابط واتساب ينقل النص فقط.')}</p>
                {notice && <p role="status">{notice}</p>}
                <label>نص الرسالة {share.allowEdit ? '(قابل للتعديل)' : '(قالب ثابت)'}
                    <textarea data-testid="whatsapp-share-message-input" value={message} onChange={e => share.allowEdit && setMessage(e.target.value)} readOnly={!share.allowEdit} maxLength={4000} rows={5} />
                </label>
                <label>رقم واتساب — تعديل مؤقت لا يغيّر ملف العميل
                    <input type="tel" autoComplete="off" inputMode="tel" data-testid="whatsapp-share-phone-input" dir="ltr" value={phone} onChange={e => setPhone(e.target.value)} />
                </label>
                <p>{norm.valid ? norm.e164 : 'أدخل رقمًا صحيحًا لفتح واتساب.'}</p>
                <p>فتح واتساب أو إغلاق نافذة المشاركة لا يؤكد الإرسال أو التسليم. رابط النص لا يرفق الملف تلقائيًا.</p>
            </>}
        </div>
        {share.stage === 'ready' && <footer className="document-actions document-share-footer">
            <a href={busy ? undefined : whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-share-wame-button"
                aria-disabled={!whatsappUrl || busy} className="document-button document-primary"
                onClick={event => { if (!whatsappUrl || busy) event.preventDefault(); else openWhatsApp(); }}>فتح واتساب — النص فقط</a>
            <button type="button" onClick={copy}>نسخ نص الرسالة</button>
        </footer>}
    </DocumentOverlay>;
}
